// Modules
import Head from "next/head";

// Components
import ChosenFilters from "components/searchPage/SearchResults/ChosenFilters";
import QueryFilter from "components/searchPage/SearchResults/QueryFilter";
import ActiveProduct from "components/searchPage/Products/ActiveProduct";
import InactiveProduct from "components/searchPage/Products/InactiveProduct";
import Pagination from "components/searchPage/Pagination/Pagination";

// Redux
import {useAppSelector} from "redux/hooks";
import {suggestionsSearchResultSelector} from "redux/searchPage/searchBar/searchBarSlice";
import {endedCourseSelector, sortingSelector} from "redux/searchPage/showResults/showResultsSlice";
import {seoInfoPageSelector} from "redux/searchPage/seoInformations/seoInformationsSlice";
import {urlPathSelector} from "redux/searchPage/filters/filtersSlice";

// Constants
import {activeCourseProductStates, ProductState, Sorting} from "constants/searchPage/gpoSearchEnum";
import {QueryStringPrimaryKeys} from "constants/searchPage/urlEnum";

// Hooks
import useSearchResults from "hooks/searchPage/useSearchResults";

// Utils
import {showHideLocations} from "components/searchPage/SearchResults/utils";
import {getQueryStringParamsByKeys, hasQuerySearch} from "utils/searchPage/queryString";

interface SearchResultsProps {
	isFilterOpen: boolean;
	setIsFilterOpen: (isFilterOpen: boolean) => void;
}

const SearchResults = ({ isFilterOpen, setIsFilterOpen }: SearchResultsProps) : JSX.Element => {
	const searchResults = useAppSelector(suggestionsSearchResultSelector);
	const seoInfoPage = useAppSelector(seoInfoPageSelector);
	const sorting = useAppSelector(sortingSelector);
	const endedCourse = useAppSelector(endedCourseSelector);
	const urlPath = useAppSelector(urlPathSelector);
	const { handleOnlyPurchaseCourse, handleCourseSorting, isPurchaseOnlyChecked} = useSearchResults();

	const isRelevanceVisible = (): boolean => {
		const querySearch = getQueryStringParamsByKeys(urlPath, QueryStringPrimaryKeys.SEARCH);
		return hasQuerySearch(querySearch);
	}

	return (
		<div className="wk-listing-page-right-content">
			{searchResults && (searchResults.totalRows === 0 || searchResults.totalRows === 1) ? (
				<Head>
					<meta name="robots" content="noindex, nofollow" />
				</Head>
			) : null}
			<h1 className="wk-lp-bold-title">{seoInfoPage.h1 || "Ipsoa Formazione"}</h1>
			<div className="wk-content">
				<p className="wk-text">{seoInfoPage.paragraph}</p>
				<hr />
				<div className="wk-choosen-filters">
					{QueryFilter()}
					{ChosenFilters()}
				</div>
				<div className={`wk-button wk-filter-button wk-icon-sliders ${isFilterOpen ? "wk-open" : ""}`} onClick={() => setIsFilterOpen(!isFilterOpen)}>
					Filtri
				</div>
				<div className="wk-found-courses">
					<div className="wk-courses-number">
						Sono stati trovati <b>{searchResults.totalRows}</b> risultati
					</div>
					<div className="wk-left-found-courses">
						<div className="wk-select-field">
							<span className="wk-sort-results-label">Ordina per</span>
							<select value={sorting} onChange={(event) => handleCourseSorting(event)}>
								<option value={Sorting.DATE}>Data</option>
								<option value={Sorting.ALPHABETICAL}>A-Z</option>
								<option value={Sorting.PRICE}>Prezzo</option>
								{isRelevanceVisible() ? <option value={Sorting.RELEVANCE}>Rilevanza</option> : ""}
							</select>
						</div>
						<label className="wk-check-container">
							<input type="checkbox" onChange={(event) => handleOnlyPurchaseCourse(event)} checked={isPurchaseOnlyChecked()} />
							<span className="wk-checkmark" />
							<div className="wk-acc-check-title">Solo corsi acquistabili</div>
						</label>
					</div>
				</div>
				{searchResults.results && searchResults.results
					.filter((result) => activeCourseProductStates.includes(result.grouping))
						.map((result) => <ActiveProduct key={result.documentId} result={result} showHideLocations={showHideLocations} />)}

				{searchResults.results && searchResults.results.filter((result) => result.grouping === ProductState.ELEARNING).length > 0 && (
						<>
							<h3 className="wk-lp-title">E-learning</h3>
							{searchResults.results
								.filter((result) => result.grouping === ProductState.ELEARNING)
								.map((result) => (
									<ActiveProduct key={result.documentId} result={result} showHideLocations={showHideLocations} />
								))}
						</>
					)}

				{searchResults.results && endedCourse.length > 0 && (
					<>
						<h3 className="wk-lp-title">Iniziative in corso o concluse</h3>
						{endedCourse.map((result) => (
							<InactiveProduct key={result.documentId} result={result} showHideLocations={showHideLocations} />
						))}
					</>
				)}
				<Pagination />
			</div>
		</div>
	);
};

export default SearchResults;
